/**
 * Geolocation Service
 * Detects user's country based on IP address for auto-selecting country code
 */

interface GeolocationData {
  country_code: string;
  country_name: string;
  city?: string;
  region?: string;
}

class GeolocationService {
  private cache: GeolocationData | null = null;
  private cacheKey = 'user_country_data';
  private cacheExpiry = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  /**
   * Get user's country code from cached data or fetch from API
   */
  async getUserCountryCode(): Promise<string> {
    // Check cache first
    const cached = this.getCachedCountry();
    if (cached) {
      console.log('📍 Using cached country:', cached);
      return cached;
    }

    // Try to detect from IP
    try {
      const countryCode = await this.detectCountryFromIP();
      console.log('📍 Detected country from IP:', countryCode);
      return countryCode;
    } catch (error) {
      console.warn('Failed to detect country, using default:', error);
      return this.getDefaultCountry();
    }
  }

  /**
   * Detect country from IP address using free geolocation API
   */
  private async detectCountryFromIP(): Promise<string> {
    try {
      // Try ipapi.co (free, 30k requests/month, no key needed)
      const response = await fetch('https://ipapi.co/json/', {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Geolocation API failed');
      }

      const data: GeolocationData = await response.json();

      if (data.country_code) {
        // Cache the result
        this.setCachedCountry(data);
        return data.country_code;
      }

      throw new Error('No country code in response');
    } catch (error) {
      console.warn('IP geolocation failed:', error);

      // Fallback: Try browser locale/timezone
      return this.detectFromBrowserLocale();
    }
  }

  /**
   * Fallback: Detect country from browser locale/timezone
   */
  private detectFromBrowserLocale(): string {
    try {
      // Try to get country from browser locale
      const locale = navigator.language || (navigator as any).userLanguage;

      if (locale) {
        // Format is usually: en-US, zh-CN, fil-PH, etc.
        const parts = locale.split('-');
        if (parts.length > 1) {
          const countryCode = parts[1].toUpperCase();
          console.log('📍 Detected from browser locale:', countryCode);
          return countryCode;
        }
      }

      // Try timezone (rough approximation)
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (timezone) {
        const countryCode = this.mapTimezoneToCountry(timezone);
        if (countryCode) {
          console.log('📍 Detected from timezone:', countryCode);
          return countryCode;
        }
      }
    } catch (error) {
      console.warn('Browser locale detection failed:', error);
    }

    return this.getDefaultCountry();
  }

  /**
   * Map timezone to country (rough approximation for common cases)
   */
  private mapTimezoneToCountry(timezone: string): string | null {
    const timezoneMap: Record<string, string> = {
      'Asia/Manila': 'PH',
      'Asia/Singapore': 'SG',
      'Asia/Kuala_Lumpur': 'MY',
      'Asia/Bangkok': 'TH',
      'Asia/Jakarta': 'ID',
      'Asia/Tokyo': 'JP',
      'Asia/Shanghai': 'CN',
      'Asia/Seoul': 'KR',
      'Asia/Kolkata': 'IN',
      'America/New_York': 'US',
      'America/Los_Angeles': 'US',
      'America/Chicago': 'US',
      'Europe/London': 'GB',
      'Australia/Sydney': 'AU'
    };

    return timezoneMap[timezone] || null;
  }

  /**
   * Get default country (Philippines)
   */
  private getDefaultCountry(): string {
    return 'PH';
  }

  /**
   * Get cached country data if valid
   */
  private getCachedCountry(): string | null {
    try {
      const cached = localStorage.getItem(this.cacheKey);
      if (!cached) return null;

      const data = JSON.parse(cached);
      const now = Date.now();

      // Check if cache is expired
      if (data.timestamp && (now - data.timestamp) < this.cacheExpiry) {
        return data.country_code;
      }

      // Cache expired, remove it
      localStorage.removeItem(this.cacheKey);
      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Cache country data
   */
  private setCachedCountry(data: GeolocationData): void {
    try {
      const cacheData = {
        country_code: data.country_code,
        country_name: data.country_name,
        timestamp: Date.now()
      };
      localStorage.setItem(this.cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Failed to cache country data:', error);
    }
  }

  /**
   * Clear cached country data (useful for testing)
   */
  clearCache(): void {
    localStorage.removeItem(this.cacheKey);
  }
}

export default new GeolocationService();
